import {
  BriefAdvancedSearchData,
  useDeleteAdvancedSearch,
  useGetAdvancedSearchByResource,
} from "@/api/advanced-search";
import { ColumnDef, Row } from "@tanstack/react-table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { TrashIcon } from "lucide-react";
import { DataTable } from "../ui/datatable";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { useCallback, useMemo } from "react";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

const baseColumns = (t: TFunction): ColumnDef<BriefAdvancedSearchData>[] => [
  {
    id: "name",
    size: 1000,
    header: t("savedAdvancedSearchName"),
    cell: ({ row }) => (
      <>
        <span>{row.original.name}</span>
      </>
    ),
  },
  {
    id: "createdAt",
    size: 1,
    header: t("createdAt"),
    cell: ({ row }) => (
      <>
        <span>
          {new Date(
            row.original.createdAt as unknown as string,
          ).toLocaleDateString()}
        </span>
      </>
    ),
  },
  {
    id: "shared",
    size: 1,
    header: t("shared"),
    cell: ({ row }) => (
      <div className="flex flex-row justify-center">
        <Badge variant={row.original.shared ? "published" : "draft"}>
          {row.original.shared ? t("oui") : t("non")}
        </Badge>
      </div>
    ),
  },
];

export const SavedAdvancedSearchQueriesList = ({
  resource,
}: {
  resource: string;
}) => {
  const { data } = useGetAdvancedSearchByResource(resource);

  const { t } = useTranslation();

  const advancedSearch = useAdvancedSearch();

  const deleteMutation = useDeleteAdvancedSearch();

  function onRowClick(row: Row<BriefAdvancedSearchData>) {
    const element = row.original;

    advancedSearch.setAdvancedSearchQuery({
      fields: element.query.fields,
      searchMode: element.query.searchMode,
      name: element.name,
    });
  }

  const onDelete = useCallback(
    (row: Row<BriefAdvancedSearchData>) => {
      deleteMutation.mutate(row.original.id, {
        onError: (e) => {
          console.error("error when deleting the AdvancedSearch :", e);
          toast({
            title: "Error",
            description: "deleteAdvancedSearchErrorMessage",
            duration: 1000,
            variant: "error",
          });
        },
      });
    },
    [deleteMutation],
  );

  const columns = useMemo(
    () => [
      ...baseColumns(t),
      {
        id: "delete",
        size: 1,
        header: "",
        cell: ({ row }) => (
          <Button type="button" variant={"link"} onClick={() => onDelete(row)}>
            <TrashIcon className="text-gray-500" />
          </Button>
        ),
      },
    ],
    [onDelete, t],
  );

  return (
    <div className="flex flex-col gap-6">
      <h2 className="font-semibold">{t("storedSearch")}</h2>
      <DataTable columns={columns} data={data ?? []} onRowClick={onRowClick} />
    </div>
  );
};
